import axios from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config/api';
import './Dashboard.css';

const SurveyProfiler = ({ user }) => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentAnswer, setCurrentAnswer] = useState([]);  // Initialize as empty array for checkboxes
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [apiData, setApiData] = useState({ answeredCount: 0, totalCount: 0 });

  useEffect(() => {
    fetchProfileQuestions();
  }, []);

  useEffect(() => {
    // Load previous answer - if corrupted checkbox data, clear it completely
    if (questions.length > 0 && currentQuestionIndex < questions.length) {
      const currentQuestion = questions[currentQuestionIndex];
      let answer = currentQuestion.userAnswer;

      // For checkbox questions
      if (currentQuestion.questionType === 'checkbox') {
        if (!answer || !Array.isArray(answer)) {
          answer = [];
        } else if (answer.length > 1) {
          // Check if data is corrupted (both "None" and regular options)
          const isNoneOption = (optionValue) => {
            const opt = currentQuestion.options?.find(o => (o.value || o.label) === optionValue);
            if (!opt) return false;
            const label = (opt.label || opt.value).toLowerCase().trim();
            return label.includes('none');
          };

          const hasNone = answer.some(isNoneOption);
          const hasRegular = answer.some(ans => !isNoneOption(ans));

          // If corrupted, clear everything and let user select again
          if (hasNone && hasRegular) {
            console.warn('⚠️ Corrupted data detected - clearing all selections');
            answer = [];
          }
        }
      }

      setCurrentAnswer(answer);
    }
  }, [currentQuestionIndex, questions]);

  const fetchProfileQuestions = async () => {
    try {
      const token = localStorage.getItem('token');
      // Add cache-busting parameter to force fresh data
      const response = await axios.get(`${API_URL}/qualifications/country-questions?t=${Date.now()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      // Don't modify fetched data - just load it as-is
      // The useEffect will handle corrupted data by clearing it
      setQuestions(response.data.questions);
      setApiData({
        answeredCount: response.data.answeredCount,
        totalCount: response.data.totalCount
      });

      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load profile questions');
      setLoading(false);
    }
  };

  const handleAnswerChange = (answer) => {
    setCurrentAnswer(answer);
  };

  const handleNext = async () => {
    if (!currentAnswer) {
      setError('Please select an answer before proceeding');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const currentQuestion = questions[currentQuestionIndex];

      const response = await axios.post(
        `${API_URL}/qualifications/submit-answer`,
        {
          questionId: currentQuestion._id,
          question_id: currentQuestion.question_id,
          question_text: currentQuestion.question,
          answer: currentAnswer
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess('Answer saved!');

      // Update the answered count in apiData
      setApiData(prev => ({
        ...prev,
        answeredCount: prev.answeredCount + 1
      }));

      // STEP 2: Check if answer triggered skip logic - refetch questions if so
      const hasSkipLogic = response.data.skipQuestions && response.data.skipQuestions.length > 0;

      if (hasSkipLogic) {
        // Skip logic was applied - refetch all questions to get updated list
        console.log('Skip logic detected, refetching questions...', response.data.skipQuestions);
        await fetchProfileQuestions();
        setCurrentQuestionIndex(0);
        setCurrentAnswer(null);
        setSuccess('Answer saved! Questions updated based on your response.');
      } else {
      // No skip logic - proceed normally
        // Remove the answered question from the list to prevent re-showing
        const updatedQuestions = questions.filter((_, index) => index !== currentQuestionIndex);
        setQuestions(updatedQuestions);

        // Move to next question after a short delay
        setTimeout(() => {
          if (updatedQuestions.length > 0) {
            // Stay at the same index (which now shows the next question)
            // Reset to index 0 if we're at or beyond the end
            if (currentQuestionIndex >= updatedQuestions.length) {
              setCurrentQuestionIndex(0);
            }
            setCurrentAnswer(null);
            setSuccess('');
          }
          // NOTE: Redirect is handled by render guard below - not here
        }, 500);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save answer');
    } finally {
      setSaving(false);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setError('');
      setSuccess('');
    }
  };

  const handleSkip = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setCurrentAnswer(null);
      setError('');
      setSuccess('');
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading profile questions...</p>
        </div>
      </div>
    );
  }

  const calculateProgress = () => {
    // Use API data for accurate counts
    const totalCount = apiData.totalCount;
    const remainingQuestions = questions.length;

    // Current question number in the overall sequence = answered + current position + 1
    const currentQuestionNumber = apiData.answeredCount + currentQuestionIndex + 1;

    // Overall progress based on how many questions have been answered out of total
    const overallProgress = totalCount > 0 ? (apiData.answeredCount / totalCount) * 100 : 0;

    return {
      answeredCount: apiData.answeredCount,
      totalCount,
      currentQuestionNumber,
      remainingQuestions,
      currentProgress: overallProgress,
      percentage: overallProgress
    };
  };

  const groupQuestionsByCategory = () => {
    const grouped = {};
    return grouped;
  };

  const handleContinueToDashboard = () => {
    navigate('/dashboard');
  };

  const progress = calculateProgress();
  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Survey Profiler</h1>
        {apiData.answeredCount < 30 ? (
          <p><strong>Welcome!</strong> Please complete these initial 30 questions to set up your profile. After completion, you'll access category-specific questions.</p>
        ) : (
            <p>Complete your profile to receive more relevant survey opportunities</p>
        )}
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {questions.length > 0 && (
        <div className="progress-section">
          <div className="progress-info">
            <span className="progress-label">Question {progress.currentQuestionNumber} of {progress.totalCount}</span>
            <span className="progress-count">{progress.answeredCount} answered</span>
          </div>
          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{ width: `${progress.percentage}%` }}
            ></div>
          </div>
          <div className="progress-percentage">{Math.round(progress.percentage)}% Progress</div>
        </div>
      )}

      <div className="profile-questions-section">
        {questions.length === 0 ? (
          <div className="no-data">
            <p>No profile questions available at the moment.</p>
            {apiData.answeredCount > 0 && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleContinueToDashboard}
                style={{
                  marginTop: '20px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                Go to Dashboard
              </button>
            )}
          </div>
        ) : currentQuestion ? (
          <div className="single-question-container">
            <div className="question-card active-question">
              <div key={currentQuestion._id}>
                <div className="question-header">
                    <h3>{currentQuestion.question}</h3>
                    {currentQuestion.isAnswered && (
                      <span className="badge badge-success">Previously Answered</span>
                    )}
                    {currentQuestion.isRequired && (
                      <span className="badge badge-required">Required</span>
                    )}
                  </div>

                  <div className="question-body">
                    {/* Radio (Single Choice) */}
                    {currentQuestion.questionType === 'radio' && (
                      <div className="radio-group">
                        {currentQuestion.options?.map((option, index) => (
                          <label key={index} className="radio-label">
                            <input
                              type="radio"
                            name={currentQuestion._id}
                            value={option.value || option.label}
                            checked={currentAnswer === (option.value || option.label)}
                            onChange={(e) => handleAnswerChange(e.target.value)}
                            required={currentQuestion.isRequired}
                          />
                          <span>{option.label || option.value}</span>
                        </label>
                      ))}
                      </div>
                    )}

                    {/* Checkbox (Multiple Choice) */}
                    {currentQuestion.questionType === 'checkbox' && (
                      <>
                        <button
                          type="button"
                          onClick={async () => {
                            console.log('🔥 CLEARING AND SAVING');
                            setCurrentAnswer([]);
                            try {
                              const token = localStorage.getItem('token');
                              await axios.post(
                                `${API_URL}/qualifications/submit-answer`,
                                { questionId: currentQuestion.questionId, answer: [] },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              alert('✅ Cleared! Now select your options.');
                            } catch (err) {
                              console.error(err);
                            }
                          }}
                          style={{
                            display: 'none',
                            marginBottom: '15px',
                            padding: '10px 20px',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer'
                          }}
                        >
                          ⚠️ CLEAR ALL SELECTIONS
                        </button>
                        <div className="checkbox-group">
                        {currentQuestion.options?.map((option, index) => {
                          // Ensure answerArray is always an array, never null/undefined
                          const answerArray = Array.isArray(currentAnswer) ? currentAnswer : [];
                          const optionValue = option.value || option.label;
                          const optionLabel = (option.label || option.value).toLowerCase().trim();

                          console.log(`Option "${option.label}": value="${optionValue}", in array=${answerArray.includes(optionValue)}`);

                          // Check if this is a "None" type option - ONLY options containing "none"
                          const isNoneOption = optionLabel.includes('none');

                          // Check if any "None" option is currently selected
                          // We need to check the VALUES in answerArray against option VALUES/LABELS
                          const noneSelected = answerArray.some(ansValue => {
                            const matchingOption = currentQuestion.options?.find(opt =>
                              (opt.value || opt.label) === ansValue
                            );
                            if (!matchingOption) return false;
                            const matchLabel = (matchingOption.label || matchingOption.value).toLowerCase().trim();
                            return matchLabel.includes('none');
                          });

                          // Check if any REGULAR (non-None) options are selected
                          const hasRegularSelections = answerArray.some(ansValue => {
                            const matchingOption = currentQuestion.options?.find(opt =>
                              (opt.value || opt.label) === ansValue
                            );
                            if (!matchingOption) return false;
                            const matchLabel = (matchingOption.label || matchingOption.value).toLowerCase().trim();
                            return !matchLabel.includes('none');
                          });

                          // Determine if this checkbox should be disabled
                          let isDisabled = false;

                          if (isNoneOption) {
                            // "None of these": should be disabled ONLY when regular options are checked
                            isDisabled = hasRegularSelections;
                          } else {
                            // Regular options: should be disabled when "None of these" is checked
                            isDisabled = noneSelected;
                          }

                          // Determine if this checkbox should be checked
                          const isChecked = answerArray.includes(optionValue);



                          return (
                            <label key={index} className={`checkbox-label ${isDisabled ? 'disabled' : ''}`}>
                            <input
                              type="checkbox"
                                value={optionValue}
                                checked={isChecked}
                                disabled={isDisabled}
                              onChange={(e) => {
                                const value = e.target.value;
                                let newAnswers;

                                if (isNoneOption) {
                                  if (e.target.checked) {
                                    // Checking "None" - clear ALL other selections and set only "None"
                                    newAnswers = [value];
                                  } else {
                                    // Unchecking "None" - remove it and enable all options
                                    newAnswers = [];
                                  }
                                } else {
                                  if (e.target.checked) {
                                    // Checking a regular option - remove any "None" options first
                                    const filteredAnswers = answerArray.filter(ansValue => {
                                      const matchingOpt = currentQuestion.options?.find(opt =>
                                        (opt.value || opt.label) === ansValue
                                      );
                                      if (!matchingOpt) return true;
                                      const ansLabel = (matchingOpt.label || matchingOpt.value).toLowerCase().trim();
                                      return !ansLabel.includes('none');
                                    });
                                    newAnswers = [...filteredAnswers, value];
                                  } else {
                                    // Unchecking regular option - just remove it
                                    newAnswers = answerArray.filter(a => a !== value);
                                  }
                                }

                                handleAnswerChange(newAnswers);
                              }}
                            />
                            <span>{option.label || option.value}</span>
                          </label>
                        );
                      })}
                      </div>
                      </>
                    )}

                    {/* Select (Dropdown) */}
                    {currentQuestion.questionType === 'select' && (
                      <select
                        className="form-select"
                        value={currentAnswer || ''}
                        onChange={(e) => handleAnswerChange(e.target.value)}
                        required={currentQuestion.isRequired}
                      >
                        <option value="">Select an option...</option>
                        {currentQuestion.options?.map((option, index) => (
                          <option key={index} value={option.value || option.label}>
                            {option.label || option.value}
                          </option>
                      ))}
                      </select>
                    )}

                    {/* Text Input */}
                    {currentQuestion.questionType === 'text' && (
                      <input
                        type="text"
                        className="form-input"
                        value={currentAnswer || ''}
                        onChange={(e) => handleAnswerChange(e.target.value)}
                        required={currentQuestion.isRequired}
                        placeholder="Enter your answer..."
                      />
                    )}

                    {/* Number Input */}
                    {currentQuestion.questionType === 'number' && (
                      <input
                        type="number"
                        className="form-input"
                        value={currentAnswer || ''}
                        onChange={(e) => handleAnswerChange(e.target.value)}
                        required={currentQuestion.isRequired}
                        placeholder="Enter a number..."
                      />
                    )}

                    {/* Date Input */}
                    {currentQuestion.questionType === 'date' && (
                      <input
                        type="date"
                        className="form-input"
                        value={currentAnswer || ''}
                        onChange={(e) => handleAnswerChange(e.target.value)}
                        required={currentQuestion.isRequired}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="question-navigation">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handlePrevious}
                  disabled={currentQuestionIndex === 0}
                >
                  ← Previous
                </button>

                <div className="nav-center" style={{ display: 'none' }}>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={handleSkip}
                    disabled={currentQuestionIndex === questions.length - 1 || !currentQuestion.isRequired}
                  >
                    Skip
                  </button>
                </div>

              <button
                  type="button"
                className="btn btn-primary"
                  onClick={handleNext}
                  disabled={saving || !currentAnswer}
              >
                  {saving ? 'Saving...' : currentQuestionIndex === questions.length - 1 ? 'Finish' : 'Next →'}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SurveyProfiler;

